/**
 * Single per-program registry.
 *
 * One file per program at src/programs/<code>/config.js holds BOTH the
 * display identity (name, color, sidebar, columns) AND the reviewer-feedback
 * block (panel + validation-summary checks). Add a program by creating that
 * file and importing + registering it below — one file, one registration.
 *
 * Consumers:
 *   - ProgramScopeSelector  → getProgramConfig / getProgramCodes
 *   - FundingRequestDetail  → getProgramFeedbackConfig / getValidationsForSummaryCard / resolveProgram / getProgramManualChecks
 *   - ReviewerFeedbackPanel → getProgramFeedbackConfig
 */

import mapConfig from './map/config';
import wmpConfig from './wmp/config';
import iwConfig from './iw/config';
import isfrConfig from './isfr/config';
import rubiconConfig from './rubicon/config';
import pocConfig from './poc/config';
import mdfConfig from './mdf/config';
import mmpVmpConfig from './mmp_vmp/config';
import pifccConfig from './pifcc/config';

const PROGRAM_REGISTRY = {
  MAP: mapConfig,
  WMP: wmpConfig,
  IW: iwConfig,
  ISFR: isfrConfig,
  RUBICON: rubiconConfig,
  POC: pocConfig,
  MDF: mdfConfig,
  MMP_VMP: mmpVmpConfig,
  PIFCC: pifccConfig,
};

/**
 * Reviewer-feedback fallback for an unknown program: hides the panel and
 * carries no checks. The summary-card consumer (getValidationsForSummaryCard)
 * deliberately falls back to MAP instead; the panel gate wants this empty
 * shape so unknown programs hide cleanly.
 *
 * @type {import('./types').ProgramFeedbackConfig}
 */
const FEEDBACK_FALLBACK = {
  programId: 'UNKNOWN',
  feedbackEnabled: false,
  validations: [],
  decisionOptions: [],
};

// ── Display-identity accessors ────────────────────────────────────────────

export function getProgramConfig(code) {
  return PROGRAM_REGISTRY[code] || null;
}

export function getAllPrograms() {
  return Object.values(PROGRAM_REGISTRY);
}

export function getProgramCodes() {
  return Object.keys(PROGRAM_REGISTRY);
}

// ── Reviewer-feedback accessors ───────────────────────────────────────────

/**
 * Resolve the validation list for a program, honoring an optional
 * sub-program override.
 *
 * A program whose SOP branches by sub-program (e.g. WMP runs a different
 * check chain for Bulk PIF than for Misc) declares the overrides under
 * `feedback.subPrograms[<SUB_PROGRAM>].validations`. When `subProgram`
 * names a registered override, its validations win; otherwise the
 * program-level `feedback.validations` apply. Falls back cleanly for a
 * missing or unknown sub-program so Misc behaviour is unchanged.
 *
 * @param {import('./types').FeedbackBlock} feedback
 * @param {string|null|undefined} subProgram Raw sub-program code (e.g. 'WMP_PIF').
 * @returns {import('./types').ValidationOption[]}
 */
function resolveValidations(feedback, subProgram) {
  if (subProgram) {
    const override = feedback.subPrograms?.[subProgram.toUpperCase()];
    if (override?.validations) return override.validations;
  }
  return feedback.validations || [];
}

/**
 * Reviewer-feedback config for a program, flattened to the shape consumers
 * already expect ({ programId, feedbackEnabled, validations, decisionOptions }).
 * Returns FEEDBACK_FALLBACK (feedbackEnabled: false) for missing programs or
 * programs whose config omits a feedback block.
 *
 * @param {string|null|undefined} programId
 * @param {string|null|undefined} [subProgram] Sub-program code (e.g. 'WMP_PIF')
 *   to select a sub-program-specific validation list; ignored when absent or
 *   unrecognized.
 * @returns {import('./types').ProgramFeedbackConfig}
 */
export function getProgramFeedbackConfig(programId, subProgram = null) {
  if (!programId) return FEEDBACK_FALLBACK;
  const config = PROGRAM_REGISTRY[programId.toUpperCase()];
  if (!config || !config.feedback) return FEEDBACK_FALLBACK;
  return {
    programId: config.code,
    feedbackEnabled: !!config.feedback.enabled,
    validations: resolveValidations(config.feedback, subProgram),
    decisionOptions: config.feedback.decisionOptions || [],
  };
}

/**
 * Resolve a request's effective display program, tolerant of legacy
 * schema and shared-prefix challenger routing.
 *
 * Precedence:
 *   1. `routing_program_hint` — shared-prefix challenger identity
 *      stamped by producers like rubicon_validation_agent.make_decision.
 *      For challenger rows (e.g. Rubicon on FR-PIF-CASH-*), authz
 *      identity stays prefix-derived on the server (extract_program
 *      returns the incumbent IW) so IW-only reviewers keep access; the
 *      hint is the *display* identity for the UI. See ChronosLambdas
 *      CR-289836497 (list grid) and ChronosAgentCoreAgents CR-289789415
 *      (producer) for the paired storage/read model.
 *   2. `request.program` — the incumbent stamped for non-challenger
 *      rows (WMP, IW, MAP-post-rollout) or emitted by list_validations.
 *   3. `validation_data.request_data_summary.program` — pre-rollout
 *      MAP rows that omitted the top-level field.
 *   4. 'MAP' fallback for legacy rows with no signal.
 *
 * NOTE: this is DISPLAY identity, not authz identity. Server-side
 * authorization for shared-prefix rows continues to return
 * [incumbent, challenger] via authorized_programs_for_fr; do NOT
 * substitute this helper for that decision.
 *
 * @param {Object} request The fund request object from the API.
 * @returns {string} Effective display program ID.
 */
export function resolveProgram(request) {
  return (
    request?.routing_program_hint
    || request?.program
    || request?.validation_data?.request_data_summary?.program
    || 'MAP'
  );
}

/**
 * Validation list for the validation-summary card, with MAP fallback for
 * unrecognized programs.
 *
 * Behavior preserved from the pre-registry inline `PROGRAM_VALIDATIONS` map:
 * an unrecognized but truthy program ID falls back to MAP's checks rather
 * than rendering zero entries. NOT used by the panel render gate — the gate
 * reads getProgramFeedbackConfig().feedbackEnabled, which still returns the
 * empty FEEDBACK_FALLBACK so unknown programs hide the panel cleanly.
 *
 * @param {string} programId Resolved program ID.
 * @param {string|null|undefined} [subProgram] Sub-program code (e.g. 'WMP_PIF')
 *   to select a sub-program-specific validation list; ignored when absent or
 *   unrecognized.
 * @returns {import('./types').ValidationOption[]}
 */
export function getValidationsForSummaryCard(programId, subProgram = null) {
  const config = getProgramFeedbackConfig(programId, subProgram);
  if (config.validations.length > 0) return config.validations;
  return PROGRAM_REGISTRY.MAP.feedback.validations;
}

/**
 * Reviewer-facing manual-check reminders for a program (V2323707533).
 *
 * Returns [] for a missing, unknown, or reminder-less program so callers gate
 * render on length alone.
 *
 * Deliberately does NOT fall back to MAP the way getValidationsForSummaryCard
 * does. These are program-specific reviewer instructions: showing MAP's to
 * another program would assert a duty that program's SOP never imposed. The
 * fall-through matters in practice -- a program with no PROGRAM_TAB_ALLOWLIST
 * entry in FundingRequestDetail.js renders MAP's renderProjectDetails(), so an
 * empty return here is what keeps MAP's reminders off its fund requests.
 *
 * @param {string|null|undefined} programId Resolved display program ID.
 * @returns {import('./types').ManualCheckItem[]}
 */
export function getProgramManualChecks(programId) {
  if (!programId) return [];
  const config = PROGRAM_REGISTRY[programId.toUpperCase()];
  return config?.manualChecks || [];
}

export default PROGRAM_REGISTRY;
